import React, { useState, useEffect } from 'react';
import invoiceService from 'services/invoiceService';
import catalogService from 'services/catalogService';
import Swal from 'sweetalert2';
import 'styles/upload.css';

export const UploadView = ({ onUploadSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState({
    xml: null,
    pdf: null,
    factura_firmada: null,
    orden_compra: null
  });
  const [formData, setFormData] = useState({
    centro_costo_id: '',
    tipo_gasto_id: '',
    notas: ''
  });
  const [costCenters, setCostCenters] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCatalogs();
  }, []);

  const loadCatalogs = async () => {
    try {
      const [costCentersRes, expenseTypesRes] = await Promise.all([
        catalogService.getCostCenters({ activo: true }),
        catalogService.getExpenseTypes({ activo: true })
      ]);

      if (!costCentersRes.error) {
        setCostCenters(costCentersRes.data.data || []);
      }

      if (!expenseTypesRes.error) {
        setExpenseTypes(expenseTypesRes.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e, fileType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0], fileType);
    }
  };

  const handleFileSelect = (file, fileType) => {
    const validExtensions = {
      xml: ['xml'],
      pdf: ['pdf'],
      factura_firmada: ['pdf'],
      orden_compra: ['pdf']
    };

    const extension = file.name.split('.').pop().toLowerCase();

    if (!validExtensions[fileType].includes(extension)) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo inválido',
        text: `El archivo debe ser de tipo ${validExtensions[fileType].join(', ').toUpperCase()}`,
        timer: 3000
      });
      return;
    }

    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo muy grande',
        text: 'El archivo no debe exceder 10MB',
        timer: 3000
      });
      return;
    }

    setSelectedFiles(prev => ({
      ...prev,
      [fileType]: file
    }));
  };

  const removeFile = (fileType) => {
    setSelectedFiles(prev => ({
      ...prev,
      [fileType]: null
    }));
  };

  const handleSubmit = async () => {

    if (!selectedFiles.xml || !selectedFiles.pdf || !selectedFiles.factura_firmada) {
      Swal.fire({
        icon: 'error',
        title: 'Archivos faltantes',
        text: 'Debes cargar XML, PDF y Factura Firmada (obligatorios)',
        timer: 3000
      });
      return;
    }

    if (!formData.centro_costo_id || !formData.tipo_gasto_id) {
      Swal.fire({
        icon: 'error',
        title: 'Datos incompletos',
        text: 'Debes seleccionar Centro de Costo y Tipo de Gasto',
        timer: 3000
      });
      return;
    }

    try {

      setUploading(true);

      const uploadFormData = new FormData();

      uploadFormData.append('xml', selectedFiles.xml);
      uploadFormData.append('pdf', selectedFiles.pdf);
      uploadFormData.append('factura_firmada', selectedFiles.factura_firmada);

      if (selectedFiles.orden_compra) {
        uploadFormData.append('orden_compra', selectedFiles.orden_compra);
      }

      uploadFormData.append('centro_costo_id', formData.centro_costo_id);
      uploadFormData.append('tipo_gasto_id', formData.tipo_gasto_id);

      if (formData.notas) {
        uploadFormData.append('notas', formData.notas);
      }


      const response = await invoiceService.createInvoice(uploadFormData);

      if (response.error) {

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.errors?.[0] || response.message,
          timer: 4000
        });

        return;
      }

      Swal.fire({
        icon: 'success',
        title: '¡Factura cargada!',
        text: 'Tu factura ha sido recibida y está siendo procesada',
        timer: 3000,
        showConfirmButton: false
      });

      setSelectedFiles({
        xml: null,
        pdf: null,
        factura_firmada: null,
        orden_compra: null
      });

      setFormData({
        centro_costo_id: '',
        tipo_gasto_id: '',
        notas: ''
      });

      if (onUploadSuccess) {
        onUploadSuccess();
      }

    } catch (error) {

      console.log("Error completo backend:", error);

      const backendMessage =
        error?.errors?.[0] ||
        error?.message ||
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        'No se pudo cargar la factura';

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: backendMessage,
        timer: 4000
      });

    } finally {

      setUploading(false);

    }
  };


  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const FileUploadZone = ({ fileType, label, required = false }) => {
    const file = selectedFiles[fileType];
    const accept = fileType === 'xml' ? '.xml' : '.pdf';

    return (
      <div className="file-upload-zone">
        <label className="form-label">
          {label} {required && <span className="required">*</span>}
        </label>

        {!file ? (
          <div
            className={`file-drop-area ${dragActive ? 'dragover' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDrop(e, fileType)}
            onClick={() => document.getElementById(`file-${fileType}`).click()}
          >
            <div className="upload-icon">📁</div>
            <p className="upload-text">Arrastra aquí o haz clic</p>
            <p className="upload-subtext">
              {accept.toUpperCase().replace('.', '')} - Máx 10MB
            </p>
            <input
              type="file"
              id={`file-${fileType}`}
              accept={accept}
              onChange={(e) => {
                if (e.target.files[0]) {
                  handleFileSelect(e.target.files[0], fileType);
                }
              }}
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          <div className="file-selected">
            <div className="file-info">
              <span className="file-icon">
                {file.name.endsWith('.xml') ? '📋' : '📄'}
              </span>
              <div>
                <div className="file-name">{file.name}</div>
                <div className="file-size">{formatFileSize(file.size)}</div>
              </div>
            </div>
            <button
              className="btn-remove"
              onClick={(e) => {
                e.stopPropagation();
                removeFile(fileType);
              }}
              type="button"
            >
              ✕
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="upload-view">
      <div className="card">
        <div className="card-header">Cargar Nueva Factura</div>

        {/* Archivos Obligatorios */}
        <div className="upload-section">
          <h3 className="section-title">Archivos Obligatorios</h3>
          <div className="files-grid">
            <FileUploadZone
              fileType="xml"
              label="Archivo XML"
              required
            />
            <FileUploadZone
              fileType="pdf"
              label="Archivo PDF"
              required
            />
            <FileUploadZone
              fileType="factura_firmada"
              label="Factura Firmada"
              required
            />
          </div>
        </div>

        {/* Archivos Opcionales */}
        <div className="upload-section">
          <h3 className="section-title">Archivos Opcionales</h3>
          <FileUploadZone
            fileType="orden_compra"
            label="Orden de Compra"
          />
        </div>

        {/* Información Adicional */}
        <div className="upload-section">
          <h3 className="section-title">Información Adicional</h3>

          <div className="form-group">
            <label className="form-label">
              Centro de Costo <span className="required">*</span>
            </label>
            <select
              className="form-input"
              value={formData.centro_costo_id}
              onChange={(e) => setFormData({ ...formData, centro_costo_id: e.target.value })}
            >
              <option value="">Seleccionar...</option>
              {costCenters.map(cc => (
                <option key={cc.id} value={cc.id}>
                  {cc.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Tipo de Gasto <span className="required">*</span>
            </label>
            <select
              className="form-input"
              value={formData.tipo_gasto_id}
              onChange={(e) => setFormData({ ...formData, tipo_gasto_id: e.target.value })}
            >
              <option value="">Seleccionar...</option>
              {expenseTypes.map(et => (
                <option key={et.id} value={et.id}>
                  {et.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Notas (Opcional)</label>
            <textarea
              className="form-input"
              rows="3"
              placeholder="Información adicional sobre la factura..."
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              maxLength="500"
            />
          </div>
        </div>

        <div className="upload-actions">
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <span className="spinner-small"></span> Cargando...
              </>
            ) : (
              <>
                <span><i className={`bi bi-file-earmark-text`}></i></span> Subir Factura
              </>
            )}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Validaciones Automáticas</div>
        <div className="validations-grid">
          <div className="validation-item">
            <div className="validation-title">✓ RFC Emisor y Receptor</div>
            <p className="validation-description">
              Verificación automática de RFC válidos
            </p>
          </div>
          <div className="validation-item">
            <div className="validation-title">✓ Montos y Totales</div>
            <p className="validation-description">
              Validación de subtotal, IVA y total
            </p>
          </div>
          <div className="validation-item">
            <div className="validation-title">✓ Folios y UUID</div>
            <p className="validation-description">
              Detección de duplicados y validación SAT
            </p>
          </div>
          <div className="validation-item">
            <div className="validation-title">✓ Fecha de Emisión</div>
            <p className="validation-description">
              Verificación de vigencia del documento
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
